import React from 'react';
import {
  Container,
  CursorIcon,
  MoveIcon,
  TextIcon,
  NoteIcon,
  UploadIcon,
  RisSearchIcon,
  QuestionnaireIcon,
  ProtocolIcon,
  SurfaceIcon,
  Separator,
  BackgroundIcon,
  LinkIcon,
  QuestionaireFormIcon,
  PaintIcon,
} from 'components/canvas/components/side-menu/style';
import useTranslateWindowPointToCanvas from 'components/canvas/hooks/use-translate-window-point-to-canvas';

import { SurfaceNodeType } from 'graphql/types';
import useIsCanvasDraggable from 'components/canvas/hooks/use-is-canvas-draggable';
import useIsCanvasSelectable from 'components/canvas/hooks/use-is-canvas-selectable';
import useCurrentSurfacePermissions from 'components/canvas/hooks/use-current-surface-permissions';
import useSurfaceNodeModalsContext from 'routes/surface/hooks/use-surface-node-modals-context';
import useCanvasLoading from 'components/canvas/hooks/use-canvas-loading';
import { ToolContext } from '../paint-menu';
import { useLoggedUser } from 'state/user';

interface SideMenuProps {
  surface: string;
}

export default function SideMenu(props: SideMenuProps) {
  const { modalTypeShown, showNodeModal } = useSurfaceNodeModalsContext();

  const toolContext = React.useContext(ToolContext);

  // Hack just to rerender the component and fetch latest current surface
  useCanvasLoading();

  const [isCanvasDraggable, setCanvasDraggable] = useIsCanvasDraggable();

  const [isCanvasSelectable, setIsCanvasSelectable] = useIsCanvasSelectable();

  const {
    canReadSurfaceNode,
    canCreateSurfaceNode,
    canCopySurfaceNode,
    canCutSurfaceNode,
    canRepositionAny,
    canDeleteAny,
  } = useCurrentSurfacePermissions();

  const translatePointToCanvas = useTranslateWindowPointToCanvas(props.surface);

  const isSelectDisabled =
    canDeleteAny === false &&
    canCopySurfaceNode() === false &&
    canCutSurfaceNode() === false &&
    canRepositionAny === false;

  function showModal(type) {
    toolContext.setSelectedTool(null);
    if (canReadSurfaceNode()) {
      const windowCenter = {
        x: window.innerWidth / 2,
        y: window.innerHeight / 2,
      };

      const node = {
        surface: props.surface,
        position: translatePointToCanvas(windowCenter),
        type,
      };

      showNodeModal(node);
    }
  }

  function showTextNodeModal() {
    showModal(SurfaceNodeType.Text);
  }

  function showNoteNodeModal() {
    showModal(SurfaceNodeType.Note);
  }

  function showDocumentNodeModal() {
    showModal(SurfaceNodeType.Document);
  }

  function showRisSearchModal() {
    showModal('RisSearch');
  }

  function showQuestionnaireModal() {
    showModal(SurfaceNodeType.Questionnaire);
  }

  function showProtocolModal() {
    showModal(SurfaceNodeType.Protocol);
  }

  function showSurfaceModal() {
    showModal(SurfaceNodeType.SubSurface);
  }

  function showBackgroundModal() {
    showModal(SurfaceNodeType.Background);
  }

  function showLinkNodeModal() {
    showModal(SurfaceNodeType.Note);
  }

  function showFormModal() {
    showModal(SurfaceNodeType.Form);
  }

  function selectPaint() {
    toolContext.setSelectedTool('draw');
    setCanvasDraggable(false);
    setIsCanvasSelectable(false);
  }

  function toggleCanvasDragging() {
    toolContext.setSelectedTool(null);
    setIsCanvasSelectable(false);
    setCanvasDraggable(!isCanvasDraggable);
  }

  function toggleCanvasNodeDraggable() {
    toolContext.setSelectedTool(null);
    if (isSelectDisabled === false) {
      setCanvasDraggable(false);
      setIsCanvasSelectable(!isCanvasSelectable);
    }
  }

  const cannotCreate = canCreateSurfaceNode() === false;

  const [loggedUser] = useLoggedUser();

  const ris =
    loggedUser?.email.includes('@9y.co') ||
    loggedUser?.email.includes('@cortlex.at') ||
    loggedUser?.email.includes('ochsenhofer');

  return (
    <>
      <Container>
        <CursorIcon
          $isDraggable={isCanvasSelectable}
          onClick={toggleCanvasNodeDraggable}
          disabled={isSelectDisabled}
        />
        <MoveIcon
          $isDraggable={isCanvasDraggable}
          onClick={toggleCanvasDragging}
        />
        <Separator />
        <TextIcon
          onClick={showTextNodeModal}
          $isSelected={modalTypeShown === SurfaceNodeType.Text}
          disabled={cannotCreate}
        />
        <NoteIcon
          onClick={showNoteNodeModal}
          $isSelected={modalTypeShown === SurfaceNodeType.Note}
          disabled={cannotCreate}
        />
        <UploadIcon
          onClick={showDocumentNodeModal}
          $isSelected={modalTypeShown === SurfaceNodeType.Document}
          disabled={cannotCreate}
        />
        {ris && (
          <RisSearchIcon
            onClick={showRisSearchModal}
            $isSelected={modalTypeShown === 'RisSearch'}
            disabled={cannotCreate}
          />
        )}
        <QuestionnaireIcon
          onClick={showQuestionnaireModal}
          $isSelected={modalTypeShown === SurfaceNodeType.Questionnaire}
          disabled={cannotCreate}
        />
        <ProtocolIcon
          onClick={showProtocolModal}
          $isSelected={modalTypeShown === SurfaceNodeType.Protocol}
          disabled={cannotCreate}
        />
        <SurfaceIcon
          onClick={showSurfaceModal}
          $isSelected={modalTypeShown === SurfaceNodeType.SubSurface}
          disabled={cannotCreate}
        />
        <BackgroundIcon
          onClick={showBackgroundModal}
          $isSelected={modalTypeShown === SurfaceNodeType.SubSurface}
          disabled={cannotCreate}
        />

        <PaintIcon
          onClick={selectPaint}
          $isSelected={toolContext.selectedTool != null}
          // disabled={cannotCreate}
        />

        <QuestionaireFormIcon
          onClick={showFormModal}
          $isSelected={modalTypeShown === SurfaceNodeType.Form}
          // disabled={cannotCreate}
        />
        {/* <LinkIcon
          onClick={showLinkNodeModal}
          $isSelected={modalTypeShown === SurfaceNodeType.SubSurface}
          disabled={cannotCreate}
        /> */}
      </Container>
    </>
  );
}
